import * as _ from 'lodash';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getProjectScope, VulnSeverity } from '../../../../shared/issuesV2';
import * as IssuesV2Store from '../../../redux/issuesV2';
import COLORS from '../../../theme/colors';
import useCategorySummary from '../../issues/IssuesInbox/hooks/useCategorySummary';
import type { FOSSAColor } from '../../../theme/colors';

import useFetchProjectScopeVulnIssueStatuses from './useFetchIssueStatuses';

export type FacetData = {
  data: Array<{ name: string; fill: FOSSAColor; value: number }>;
  other?: number;
  total: number;
  errors?: Error[];
};

export type SummaryMetricFacetsData = {
  licensing: FacetData;
  security: FacetData;
  quality: FacetData;
  isLoading?: boolean;
  errors?: string[];
};

const useSummaryMetricFacetsData = (
  projectId: string,
  revisionId: string,
  revisionScanId?: number,
): SummaryMetricFacetsData => {
  const scope = getProjectScope(projectId, revisionId, revisionScanId);
  const categorySummary = useCategorySummary(scope);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      IssuesV2Store.actions.fetchIssueTypeSummary({
        ...scope,
        revision: revisionId,
        revisionScanId,
      }),
    );
    dispatch(IssuesV2Store.actions.fetchStatusSummary());
  }, []);

  const { data: criticalVulnIssueStatuses, error: criticalVulnIssueError } = useFetchProjectScopeVulnIssueStatuses({
    projectId,
    revisionId,
    revisionScanId: revisionScanId ?? 0,
    severity: VulnSeverity.Critical,
  });
  const { data: highVulnIssueStatuses, error: highVulnIssueError } = useFetchProjectScopeVulnIssueStatuses({
    projectId,
    revisionId,
    revisionScanId: revisionScanId ?? 0,
    severity: VulnSeverity.High,
  });
  const { data: mediumVulnIssueStatuses, error: mediumVulnIssueError } = useFetchProjectScopeVulnIssueStatuses({
    projectId,
    revisionId,
    revisionScanId: revisionScanId ?? 0,
    severity: VulnSeverity.Medium,
  });
  const { data: lowVulnIssueStatuses, error: lowVulnIssueError } = useFetchProjectScopeVulnIssueStatuses({
    projectId,
    revisionId,
    revisionScanId: revisionScanId ?? 0,
    severity: VulnSeverity.Low,
  });

  const criticalCount = criticalVulnIssueStatuses?.issues.active ?? 0;
  const highCount = highVulnIssueStatuses?.issues.active ?? 0;
  const mediumCount = mediumVulnIssueStatuses?.issues.active ?? 0;
  const lowCount = lowVulnIssueStatuses?.issues.active ?? 0;

  const issueTypesSummary = useSelector(IssuesV2Store.selectors.getIssueTypeSummary);
  const isLoading = issueTypesSummary.value === null || issueTypesSummary.isLoading || categorySummary === null;
  const licensingData = issueTypesSummary.value
    ? [
        { name: 'Denied', fill: COLORS.fossaRed110, value: issueTypesSummary.value.policy_conflict },
        { name: 'Flagged', fill: COLORS.fossaYellow, value: issueTypesSummary.value.policy_flag },
      ]
    : [];
  const unlicensedDependencyCount = issueTypesSummary.value?.unlicensed_dependency ?? 0;
  const unconcludedDependencyCount = issueTypesSummary.value?.unconcluded_dependency ?? 0;
  const outdatedDependencyCount = issueTypesSummary.value?.outdated_dependency ?? 0;
  const licensingOther = unlicensedDependencyCount + outdatedDependencyCount + unconcludedDependencyCount;
  const licensingTotal = categorySummary?.licensing ?? 0;
  const securityData = [
    { name: 'Critical', fill: COLORS.fossaRed, value: criticalCount },
    { name: 'High', fill: COLORS.fossaOrange, value: highCount },
    { name: 'Med', fill: COLORS.fossaYellow, value: mediumCount },
    { name: 'Low', fill: COLORS.fossaBlue110, value: lowCount },
  ];
  const categorySummaryVulnCount = categorySummary?.vulnerability ?? 0;
  const securityValueSum = securityData.reduce((sum, { value }) => sum + value, 0);
  const securityOther = categorySummaryVulnCount - securityValueSum;
  const securityTotal = categorySummary?.vulnerability ?? 0;

  const qualityData = issueTypesSummary.value
    ? [
        { name: 'Blocked', fill: COLORS.fossaRed120, value: issueTypesSummary.value.blacklisted_dependency },
        { name: 'Abandonware', fill: COLORS.fossaPink, value: issueTypesSummary.value.risk_abandonware },
        { name: 'Empty', fill: COLORS.fossaYellow, value: issueTypesSummary.value['risk_empty-package'] },
        { name: 'Native Code', fill: COLORS.fossaYellow60, value: issueTypesSummary.value['risk_native-code'] },
        { name: 'Outdated', fill: COLORS.fossaOrange, value: issueTypesSummary.value.outdated_dependency },
      ]
    : [];
  const qualityTotal = categorySummary?.quality ?? 0;
  const qualityValueSum = qualityData.reduce((sum, { value }) => sum + value, 0);
  const qualityOther = qualityTotal - qualityValueSum;
  return {
    isLoading,
    licensing: { data: licensingData, other: licensingOther, total: licensingTotal },
    security: {
      data: securityData,
      other: securityOther,
      total: securityTotal,
      errors: _.compact([criticalVulnIssueError, highVulnIssueError, mediumVulnIssueError, lowVulnIssueError]),
    },
    quality: { data: qualityData, total: qualityTotal, other: qualityOther },
  };
};

export default useSummaryMetricFacetsData;
